import { IdleInfo } from '../models/Idle';

/**
 * Formats a date as YYYY-MM-DD in local timezone (not UTC)
 * This prevents timezone shifts that could cause days to be missed
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatLocalDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Finds idle data matching a date, accounting for timezone differences
 * Checks the target date and adjacent days (±1) to handle UTC/UTC-5 timezone variations
 * @param date - The target date to find data for
 * @param idleData - Array of idle info data from the database
 * @returns Matching idle data item or undefined
 */
export const findIdleDataForDate = (date: Date, idleData: IdleInfo[]): IdleInfo | undefined => {
	const dateStr = formatLocalDate(date);
	
	// Try exact match first
	let dayIdleData = idleData.find(item => {
		if (item.summaryDate === dateStr) return true;
		const itemDateOnly = item.summaryDate.split('T')[0];
		return itemDateOnly === dateStr;
	});
	
	if (dayIdleData) return dayIdleData;
	
	// Check previous day (timezone shift backward)
	const prevDate = new Date(date);
	prevDate.setDate(date.getDate() - 1);
	const prevDateStr = formatLocalDate(prevDate);
	dayIdleData = idleData.find(item => {
		if (item.summaryDate === prevDateStr) return true;
		const itemDateOnly = item.summaryDate.split('T')[0];
		return itemDateOnly === prevDateStr;
	});
	
	if (dayIdleData) return dayIdleData;
	
	// Check next day (timezone shift forward)
	const nextDate = new Date(date);
	nextDate.setDate(date.getDate() + 1);
	const nextDateStr = formatLocalDate(nextDate);
	dayIdleData = idleData.find(item => {
		if (item.summaryDate === nextDateStr) return true;
		const itemDateOnly = item.summaryDate.split('T')[0];
		return itemDateOnly === nextDateStr;
	});
	
	return dayIdleData;
};

/**
 * Returns today's date normalized to midnight (00:00:00.000)
 * @param referenceDate - Optional reference date (defaults to new Date())
 * @returns Date object set to midnight
 */
export const getTodayAtMidnight = (referenceDate?: Date): Date => {
	const today = referenceDate ? new Date(referenceDate) : new Date();
	today.setHours(0, 0, 0, 0);
	return today;
};

/**
 * Returns yesterday's date normalized to midnight (00:00:00.000)
 * @param referenceDate - Optional reference date (defaults to new Date())
 * @returns Date object for yesterday at midnight
 */
export const getYesterday = (referenceDate?: Date): Date => {
	const today = getTodayAtMidnight(referenceDate);
	const yesterday = new Date(today);
	yesterday.setDate(today.getDate() - 1);
	return yesterday;
};

/**
 * Returns the Sunday of the current week
 * @param referenceDate - Optional reference date (defaults to new Date())
 * @returns Date object for this Sunday at midnight
 */
export const getThisSunday = (referenceDate?: Date): Date => {
	const today = getTodayAtMidnight(referenceDate);
	const thisSunday = new Date(today);
	thisSunday.setDate(today.getDate() - today.getDay());
	return thisSunday;
};

/**
 * Returns the Sunday of the previous week
 * @param referenceDate - Optional reference date (defaults to new Date())
 * @returns Date object for last Sunday at midnight
 */
export const getLastSunday = (referenceDate?: Date): Date => {
	const thisSunday = getThisSunday(referenceDate);
	const lastSunday = new Date(thisSunday);
	lastSunday.setDate(thisSunday.getDate() - 7);
	return lastSunday;
};

/**
 * Returns the Saturday of the current week
 * @param referenceDate - Optional reference date (defaults to new Date())
 * @returns Date object for this Saturday at midnight
 */
export const getThisSaturday = (referenceDate?: Date): Date => {
	const thisSunday = getThisSunday(referenceDate);
	const thisSaturday = new Date(thisSunday);
	thisSaturday.setDate(thisSunday.getDate() + 6);
	return thisSaturday;
};

